import clsx from 'clsx';
import React, {
  ChangeEvent,
  cloneElement,
  ReactElement,
  ReactNode,
} from 'react';

import { Icon } from '../icon/Icon';
import { TooltipProps } from '../tooltip/Tooltip';
import styles from './Checkbox.module.scss';

export type CheckboxVariant = 'primary' | 'critical' | 'disabled';

export interface CheckBoxProps {
  /** Extra class forwarded to the wrapper. */
  className?: string;
  /** DOM id; pairs the visible label with the underlying `<input>` via `htmlFor`. */
  id?: string;
  /**
   * Optional `<Tooltip>` element to wrap the label in. The tooltip's
   * `children` is supplied internally; placement defaults to
   * `'top-start'` if not specified on the passed-in tooltip.
   */
  tooltip?: ReactElement<Omit<TooltipProps, 'children'>>;
  /** Test selector forwarded as `data-testid` on the wrapper. Defaults to `'custom-checkbox'`. */
  testId?: string;
  /** When true, renders the error treatment and shows `errorText` below the label. */
  hasError?: boolean;
  /** Error message rendered below the label when `hasError` is true. */
  errorText?: ReactNode;
  /** Name of the checkbox; passed to the `<input>` and to the `onChange` callback. */
  name: string;
  /** Visible label rendered to the right of the checkbox. */
  label: React.ReactNode;
  /** Called with `(name, event)` when the user toggles the checkbox. */
  onChange?: (name: string, event: ChangeEvent<HTMLInputElement>) => void;
  /** Controlled checked state. Defaults to `false`. */
  checked?: boolean;
  /** When true, replaces the check with a lock icon and disables interaction. */
  locked?: boolean;
  /** Indeterminate state — renders a `−` (minus) icon instead of `✓` (checkmark). */
  partial?: boolean;
  /** Color treatment: `'primary'` (default), `'critical'`, or `'disabled'` (also disables the input). */
  variant?: CheckboxVariant;
  /** Size preset. Defaults to `'medium'`. */
  size?: 'medium' | 'small';
  'aria-label'?: string;
}

/**
 * Single checkbox with label, optional tooltip, and indeterminate
 * (`partial`) and locked states.
 *
 * Always rendered as a controlled input — pass `checked` and use
 * `onChange` to update it. For groups of related options use
 * `RadioGroup` (mutually exclusive) or render multiple `CheckBox`es.
 */
export const CheckBox = ({
  className,
  onChange,
  label,
  name,
  id,
  tooltip,
  checked = false,
  locked = false,
  partial = false,
  variant = 'primary',
  size = 'medium',
  testId = 'custom-checkbox',
  hasError,
  errorText,
  ...rest
}: CheckBoxProps) => {
  const Label = tooltip ? (
    cloneElement(
      tooltip,
      {
        placement: tooltip.props.placement ?? 'top-start',
      },
      <span
        className={clsx(styles.checkboxLabel, styles.checkboxLabelWithTooltip)}
        data-checkbox-label
      >
        {label}
      </span>
    )
  ) : (
    <span className={styles.checkboxLabel} data-checkbox-label>
      {label}
    </span>
  );

  const iconName = partial ? 'Minus' : 'Checkmark';

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange?.(name, event);

  return (
    <div
      data-testid={testId}
      className={clsx(styles.wrapper, className)}
      data-checkbox-wrapper
    >
      <label
        htmlFor={id}
        className={clsx(
          styles.checkbox,
          styles[variant],
          size === 'medium' ? styles.sizeMedium : styles.sizeSmall,
          { [styles.checked]: checked, [styles.locked]: locked }
        )}
        data-checkbox
      >
        <input
          className={styles.checkboxElement}
          id={id}
          data-testid={id}
          type="checkbox"
          checked={checked}
          aria-checked={checked}
          name={name}
          onChange={onInputChange}
          disabled={variant === 'disabled' || locked}
          readOnly
          {...rest}
        />
        {locked ? (
          <Icon className={styles.lockIcon} iconName="LockLight" size={20} />
        ) : (
          <div className={styles.checkmark} data-checkmark>
            <Icon
              className={styles.checkIcon}
              iconName={iconName}
              size={16}
              data-testid="check"
              data-check-icon
            />
          </div>
        )}
        {Label}
      </label>
      {hasError && errorText && (
        <p className={styles.errorText} data-testid="error-text">
          {errorText}
        </p>
      )}
    </div>
  );
};
